import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameResult } from "@topzle/types";
import { z } from "zod";

const safeTruths = [
  "What skill would you love to learn?",
  "What always makes you laugh?",
  "Which trip do you remember most?",
];
const safeDares = [
  "Do your best robot dance for ten seconds.",
  "Speak like a sports commentator for one minute.",
  "Draw a cat without looking at the screen.",
];
export const truthOrDareStateSchema = z.object({
  players: z.array(z.string()),
  activeIndex: z.number().int(),
  prompt: z.string(),
  kind: z.enum(["truth", "dare"]),
  phase: z.enum(["choosing", "prompt", "finished"]),
  rejectedBy: z.array(z.string()),
  completed: z.record(z.string(), z.number()),
  turn: z.number(),
});
export const truthOrDareEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("choose"), kind: z.enum(["truth", "dare"]) }),
  z.object({ type: z.literal("complete") }),
  z.object({ type: z.literal("skip") }),
  z.object({ type: z.literal("reject") }),
  z.object({ type: z.literal("finish") }),
]);
export const truthOrDareSettingsSchema = z.object({
  mode: z.enum(["family", "friends", "party"]).default("family"),
  maximumTurns: z.number().int().min(1).max(100).default(12),
});
type State = z.infer<typeof truthOrDareStateSchema>;
type Event = z.infer<typeof truthOrDareEventSchema>;
type Settings = z.infer<typeof truthOrDareSettingsSchema>;
export const truthOrDareGame: TopzleGameDefinition<State, Event, Settings> = {
  id: "truth-or-dare",
  version: "1.0.0",
  name: "AI Truth or Dare",
  shortDescription: "Safe, age-aware prompts with skip, reject, and report controls.",
  longDescription:
    "A conversation-first prompt game with curated safe defaults and validated AI rewriting boundaries.",
  category: "social",
  icon: "sparkles",
  cover: "truth-or-dare",
  minPlayers: 2,
  maxPlayers: 12,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: false,
  supportsLocalAi: true,
  supportsCloudAi: true,
  supportsTournaments: false,
  supportsPersistence: true,
  requiredCapabilities: ["audio"],
  supportedLayouts: ["grid", "active_speaker", "avatar_only"],
  preferredLayout: "active_speaker",
  stateSchema: truthOrDareStateSchema,
  eventSchema: truthOrDareEventSchema,
  settingsSchema: truthOrDareSettingsSchema,
  createInitialState(context) {
    return {
      players: context.playerIds,
      activeIndex: 0,
      prompt: "",
      kind: "truth",
      phase: "choosing",
      rejectedBy: [],
      completed: Object.fromEntries(context.playerIds.map((id) => [id, 0])),
      turn: 0,
    };
  },
  validateEvent(state, event, context) {
    const active = state.players[state.activeIndex];
    if (event.type === "reject")
      return { valid: state.phase === "prompt" && Boolean(context.actorId) };
    return {
      valid: context.actorId === active && state.phase !== "finished",
      code: "not_active_player",
      message: "Only the active player can do that",
    };
  },
  reduceEvent(state, event, context) {
    if (event.type === "finish") return { ...state, phase: "finished" };
    if (event.type === "reject")
      return context.actorId
        ? { ...state, rejectedBy: [...new Set([...state.rejectedBy, context.actorId])] }
        : state;
    if (event.type === "choose") {
      const list = event.kind === "truth" ? safeTruths : safeDares;
      return {
        ...state,
        kind: event.kind,
        prompt: list[state.turn % list.length] ?? list[0] ?? "Share something fun.",
        phase: "prompt",
        rejectedBy: [],
      };
    }
    const completed = { ...state.completed };
    const active = state.players[state.activeIndex];
    if (event.type === "complete" && active) completed[active] = (completed[active] ?? 0) + 1;
    return {
      ...state,
      completed,
      prompt: "",
      phase: "choosing",
      activeIndex: (state.activeIndex + 1) % state.players.length,
      turn: state.turn + 1,
      rejectedBy: [],
    };
  },
  getPublicState: (state) => state,
  getPrivatePlayerState: () => ({}),
  getSpectatorState: (state) => state,
  getAvailableActions: (state, playerId) =>
    playerId === state.players[state.activeIndex]
      ? state.phase === "choosing"
        ? [
            { type: "choose", kind: "truth" },
            { type: "choose", kind: "dare" },
          ]
        : [{ type: "complete" }, { type: "skip" }]
      : state.phase === "prompt"
        ? [{ type: "reject" }]
        : [],
  calculateResult(state): GameResult | null {
    return state.phase === "finished"
      ? { status: "completed", winnerIds: [], scores: state.completed, reason: "host_ended" }
      : null;
  },
  handlePlayerJoin(state, arrival) {
    if (
      state.phase === "finished" ||
      state.players.length >= 12 ||
      state.players.includes(arrival.playerId)
    )
      return null;
    return {
      state: {
        ...state,
        players: [...state.players, arrival.playerId],
        completed: { ...state.completed, [arrival.playerId]: 0 },
      },
    };
  },
  handlePlayerDeparture(state, departure) {
    const removedIndex = state.players.indexOf(departure.playerId);
    if (removedIndex < 0 || state.players.length <= 1) return null;
    const players = state.players.filter((id) => id !== departure.playerId);
    const removedActivePlayer = removedIndex === state.activeIndex;
    const completed = { ...state.completed };
    delete completed[departure.playerId];
    return {
      state: {
        ...state,
        players,
        completed,
        activeIndex: removedActivePlayer
          ? removedIndex % players.length
          : removedIndex < state.activeIndex
            ? state.activeIndex - 1
            : state.activeIndex,
        prompt: removedActivePlayer ? "" : state.prompt,
        phase: removedActivePlayer && state.phase !== "finished" ? "choosing" : state.phase,
        rejectedBy: state.rejectedBy.filter((id) => id !== departure.playerId),
      },
    };
  },
};
